/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Check, X, Upload, FileText, Download } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { notificationServices } from '@/Services';
import { purchaseRequestServices } from '@/Services/PurchaseRequestServices/PurchaseRequestService';
import processFlowServices from '@/Services/Analytics Services/process-flow.services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { modalActions } from '@/Actions';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { getHigherUnitName, convertWeightUnit, imageToBase64, useProductData } from '@/Helpers';
import { AlertModal } from '@/Components/Modals';
import { PRDocPreview, downloadPrDoc } from './PRDocViewer';

const columns = [
    {
        title: 'S.No',
        key: 'sno',
        width: 40,
        customCol: true,
    },
    {
        title: 'Product Name',
        key: 'productName',
        width: 150,
        customCol: true,
    },
    {
        title: 'SKU Code',
        key: 'skuCode',
        width: 100,
        customCol: true,
    },
    {
        title: 'Qty',
        key: 'qty',
        width: 100,
        customCol: true,
    },
    {
        title: 'Pending GRN Qty',
        key: 'pendingGrnQty',
        width: 110,
        customCol: true,
    },
    {
        title: 'Open PR Qty',
        key: 'openPrQty',
        width: 100,
        customCol: true,
    },
    {
        title: 'Pending PR Approval Qty',
        key: 'pendingPrApprovalQty',
        width: 110,
        customCol: true,
    },
    {
        title: 'Remarks',
        key: 'remarks',
        width: 90,
        customCol: true,
    },
];

function PRApprovalModalContent({ rowData, refetch }) {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [reason, setReason] = useState('');
    const [uploadedDoc, setUploadedDoc] = useState(null);
    const [uploadedDocUrl, setUploadedDocUrl] = useState('');
    const [showDocPanel, setShowDocPanel] = useState(false);
    const [skuStockInfo, setSkuStockInfo] = useState([]);

    const storeList = useSelector((state) => state.form?.FILTERED_STORES?.data?.LIST || []);
    const productList = useSelector((state) => state.form?.ALL_PRODUCTS?.data?.LIST || []);
    // Populated by PurchaseRequestList (state.wastage.usersList) before this modal opens.
    const usersList = useSelector((state) => state.wastage?.usersList || []);

    useEffect(() => {
        if (productList?.length === 0) {
            useProductData(dispatch);
        }
    }, []);

    const { data: detailResponse, isLoading: isLoadingDetail } = useQuery({
        queryKey: ['purchaseRequestDetail', rowData?.id],
        queryFn: () => purchaseRequestServices.fetchPurchaseRequestDetail({ data: { prId: rowData?.id } }),
        enabled: !!rowData?.id,
    });

    const prDetail = detailResponse?.data || rowData;

    const { data: processNodes } = useQuery({
        queryKey: ['processNodes', prDetail?.storeId],
        queryFn: () => processFlowServices.fetchNodes({ params: { storeId: prDetail?.storeId } }),
        enabled: !!prDetail?.storeId,
    });

    const storeName = useMemo(() => {
        const store = storeList.find(s => s.id === prDetail?.storeId);
        return store?.name || '-';
    }, [storeList, prDetail?.storeId]);

    const processName = useMemo(() => {
        const nodes = processNodes?.data?.nodes || [];
        const node = nodes.find(n => n.id?.toString() === prDetail?.processId?.toString());
        return node ? `${node.data?.heading} - ${node.data?.sectionName}` : '-';
    }, [processNodes, prDetail?.processId]);

    // Name of the user who raised the PR (requestedBy holds the user id).
    const raisedByName = useMemo(() => {
        const uid = prDetail?.requestedBy ?? rowData?.requestedBy;
        return usersList.find((u) => u.id === uid)?.displayName || '-';
    }, [usersList, prDetail?.requestedBy, rowData?.requestedBy]);

    const fetchSkuStock = async (storeId) => {
        if (!storeId) {
            setSkuStockInfo([]);
            return;
        }
        try {
            const response = await inventoryAnalyticsServices.skuStock({
                params: {
                    type: 'stock',
                    storeId: storeId,
                },
            });
            const variantCurrentStockQtyMap = {};
            Object.entries(response?.data || {})?.forEach(([, value]) => {
                value?.currentStock?.forEach((ele) => {
                    variantCurrentStockQtyMap[ele?.variantId] = ele?.rawQty;
                });
            });
            setSkuStockInfo([
                {
                    name: 'Available',
                    bgColor: '#abc3',
                    color: 'black',
                    values: variantCurrentStockQtyMap,
                },
            ]);
        } catch (error) {
            console.error('Error fetching sku stock info', error);
        }
    };

    useEffect(() => {
        if (prDetail?.storeId) {
            fetchSkuStock(prDetail.storeId);
        }
    }, [prDetail?.storeId]);

    const renderQtyInfo = (variantId, unit) => {
        if (!skuStockInfo?.length) return null;
        const info = skuStockInfo[0];
        const val = info.values[variantId] ?? 0;
        const converted = convertWeightUnit(val, unit, undefined, true);

        return (
            <div className='flex gap-2 mt-1 ml-3'>
                <span
                    className='text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center justify-center whitespace-nowrap w-max'
                    style={{ backgroundColor: info.bgColor, color: info.color }}
                >
                    <span className='mr-1 opacity-80'>{info.name}:</span>
                    <span>
                        {(converted !== undefined && converted !== null && converted !== '') ? converted : 0}
                    </span>
                </span>
            </div>
        );
    };

    // Build a variant lookup map from productList
    const variantMap = useMemo(() => {
        const map = {};
        productList?.forEach((product) => {
            product?.variants?.forEach((variant) => {
                map[variant?.id] = {
                    productName: product?.productName,
                    displayName: variant?.displayName,
                    skuCode: variant?.clientVariantId || product?.skuCode,
                    unit: product?.unit,
                    skuType: product?.skuType,
                };
            });
        });
        return map;
    }, [productList]);

    const items = useMemo(() => {
        try {
            const itemsArray = prDetail?.items || [];
            let rawItems = itemsArray;

            if (rawItems.length === 0) {
                const data = prDetail?.jsonData;
                if (typeof data === 'string') {
                    rawItems = JSON.parse(data) || [];
                } else {
                    rawItems = data || [];
                }
            }

            return rawItems.map((item) => {
                const variantInfo = variantMap[item.entityId] || {};
                const baseUnit = variantInfo.unit || item.unit || '';
                const higherUnit = getHigherUnitName(baseUnit);
                // Per-item remarks live in the item's jsonData (may arrive as a string).
                let itemJson = item?.jsonData;
                if (typeof itemJson === 'string') {
                    try {
                        itemJson = JSON.parse(itemJson);
                    } catch (err) {
                        itemJson = {};
                    }
                }
                return {
                    ...item,
                    productName: `${item?.productName || ''}${item?.variantName ? ` (${item?.variantName})` : ''}`,
                    skuCode: item.skuCode || variantInfo.skuCode || '-',
                    unit: higherUnit || baseUnit,
                    rawUnit: baseUnit,
                    remarks: itemJson?.remarks ?? item?.remarks ?? '',
                };
            });
        } catch (e) {
            return [];
        }
    }, [prDetail?.jsonData, prDetail?.items, variantMap]);

    const filteredItems = useMemo(() => {
        return items.filter(item =>
            item.productName?.toLowerCase().includes(search.toLowerCase()) ||
            item.skuCode?.toLowerCase().includes(search.toLowerCase())
        );
    }, [items, search]);

    // Variant ids of the PR items — sent as entityIds so the backend scopes
    // the pending-grn response to just these variants.
    const prItemEntityIds = useMemo(
        () => items.map((i) => i?.entityId).filter((id) => id != null),
        [items]
    );

    const { data: pendingGrnData } = useQuery({
        queryKey: ['pendingGrn', prDetail?.storeId, prItemEntityIds],
        queryFn: () => purchaseRequestServices.fetchPendingGrn({
            data: {
                storeId: Number(prDetail?.storeId),
                subType: 'VENDOR',
                entityType: 'productVariants',
                entityIds: prItemEntityIds,
            },
        }),
        enabled: !!prDetail?.storeId && prItemEntityIds.length > 0,
    });

    // entityId in the pending-grn response is the variantId — build
    // variantId → item maps for the three qty columns.
    const pendingGrnMaps = useMemo(() => {
        const d = pendingGrnData?.data || {};
        const toMap = (list) => {
            const map = {};
            (list || []).forEach((item) => {
                if (item?.entityId != null) map[item.entityId] = item;
            });
            return map;
        };
        return {
            pendingGrn: toMap(d?.posPendingGrn),
            openPr: toMap(d?.approvedPrsNotUsedInPo),
            pendingApproval: toMap(d?.unapprovedPrs),
        };
    }, [pendingGrnData]);

    // Qtys arrive in the lower unit — convert via the row's base unit; hover
    // shows the related PO / PR ids.
    const renderPendingQtyCell = (map, row, qtyKey, infoKey, idKey) => {
        const item = map?.[row?.entityId];
        const raw = item?.[qtyKey];
        if (raw === undefined || raw === null) {
            return <span className='text-gray-400 ml-3'>-</span>;
        }
        const refIds = (item?.[infoKey] || [])
            .map((info) => info?.[idKey])
            .filter(Boolean)
            .join(', ');
        const converted = convertWeightUnit(raw, row.rawUnit, 'IND', true);
        return (
            <span className='font-medium text-gray-700 ml-3 whitespace-nowrap' title={refIds}>
                {converted ? converted : 0}
            </span>
        );
    };

    const { mutate: updateStatus, isLoading: isUpdating } = useMutation({
        mutationFn: purchaseRequestServices.updatePurchaseRequestStatus,
        onSuccess: (res) => {
            if (res.statusCode === 200) {
                notificationServices.generateNotification({
                    type: 'success',
                    message: 'Purchase Request updated successfully',
                });
                queryClient.invalidateQueries({ queryKey: ['purchaseRequests'] });
                if (refetch) refetch();
                dispatch(modalActions.toggleCustomModal(false));
            } else {
                notificationServices.generateNotification({
                    type: 'error',
                    message: res.message || 'Error updating status',
                });
            }
        },
        onError: (err) => {
            notificationServices.generateNotification({
                type: 'error',
                message: err.message || 'Operation failed',
            });
        }
    });

    const handleAction = (action) => {
        updateStatus({
            data: {
                action: action,
                entityType: 'PurchaseRequest',
                entityId: rowData.id,
                extra: {
                    uploadedDoc: uploadedDocUrl || '',
                    reason: reason,
                },
            }
        });
    };

    const showConfirmation = (action) => {
        // if (prDetail?.approvalStatus?.currentPendingDocUploadMandatory && !uploadedDoc) {
        //     notificationServices.generateNotification({
        //         type: 'error',
        //         message: 'Please upload document',
        //     });
        //     return;
        // }
        const isApprove = action === 'APPROVE';
        dispatch(modalActions.toggleAlert(true, {
            heading: isApprove ? 'Approve Purchase Request' : 'Reject Purchase Request',
            message: isApprove
                ? `Are you sure you want to approve PR ${rowData.clientPrId ? `(${rowData.clientPrId})` : ''}?`
                : `Are you sure you want to reject PR ${rowData.clientPrId ? `(${rowData.clientPrId})` : ''}?`,
            confirmText: isApprove ? 'Yes, Approve' : 'Yes, Reject',
            cancelText: 'Cancel',
            colorReverse: isApprove,
            handleConfirm: () => {
                dispatch(modalActions.toggleAlert(false));
                handleAction(action);
            },
        }));
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadedDoc(file);
            const base64Url = await imageToBase64(file);
            setUploadedDocUrl(base64Url);
        }
    };

    const handleRemoveDoc = () => {
        setUploadedDoc(null);
        setUploadedDocUrl('');
    };

    const customRowData = {
        sno: (row, index) => index + 1,
        productName: (row) => (
            <div className='flex flex-col'>
                <span className='text-gray-700 font-medium ml-3'>{row.productName}</span>
                {renderQtyInfo(row.entityId, productList?.find(p => p.id === row.productId)?.unit)}
            </div>
        ),
        skuCode: (row) => <span className='bg-gray-100 px-2 py-0.5 rounded text-[10px] font-mono ml-3'>{row.skuCode}</span>,
        qty: (row) => {
            const converted = convertWeightUnit(row.qty, row.rawUnit, 'IND', true);
            return <span className='font-bold text-gray-800 ml-3'>{converted ? converted : 0}</span>;
        },
        pendingGrnQty: (row) => renderPendingQtyCell(pendingGrnMaps.pendingGrn, row, 'pendingForGrnQty', 'poInfo', 'clientPoId'),
        openPrQty: (row) => renderPendingQtyCell(pendingGrnMaps.openPr, row, 'prAvailableQty', 'prInfo', 'generatedPrId'),
        pendingPrApprovalQty: (row) => renderPendingQtyCell(pendingGrnMaps.pendingApproval, row, 'qty', 'prInfo', 'generatedPrId'),
        remarks: (row) => (
            <span className='text-gray-600 ml-3 text-[12px] break-words' title={row.remarks || ''}>
                {row.remarks || '-'}
            </span>
        ),
    };

    return (
        <div className='flex flex-col h-full bg-white relative overflow-hidden'>
            <AlertModal />
            <div className='p-2 border-b border-gray-100'>
                <div className='flex items-center justify-between mb-2'>
                    <h2 className='text-lg font-bold text-center w-full'>View Purchase Request Details</h2>
                </div>
            </div>

            {/* Info Grid */}
            <div className='px-4 py-3 bg-gray-50/50 border-b border-gray-100'>
                <div className='grid grid-cols-7 gap-4'>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>PR Name</p>
                        <p className='text-[13px] text-gray-700 font-medium'>{prDetail?.jsonData?.description || `PR #${prDetail?.id}`}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Client PR Id</p>
                        <p className='text-[13px] text-gray-700 font-medium'>{prDetail?.clientPrId}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Procuring Store</p>
                        <p className='text-[13px] text-gray-700 font-medium truncate' title={storeName}>{storeName}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Procuring Process</p>
                        <p className='text-[13px] text-gray-700 font-medium truncate' title={processName}>{processName}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>PR Date</p>
                        <p className='text-[13px] text-gray-700 font-medium'>{prDetail?.createdAt ? new Date(prDetail?.createdAt).toISOString().split('T')[0] : '-'}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Require By Date</p>
                        <p className='text-[13px] text-gray-700 font-medium'>{prDetail?.jsonData?.requireByDate ? new Date(prDetail?.jsonData?.requireByDate).toISOString().split('T')[0] : '-'}</p>
                    </div>
                    <div className='space-y-0.5'>
                        <p className='text-[9px] font-bold text-gray-400 uppercase tracking-wider'>Raised By</p>
                        <p className='text-[13px] text-gray-700 font-medium truncate' title={raisedByName}>{raisedByName}</p>
                    </div>
                </div>
            </div>

            <div className='flex flex-1 min-h-0'>
                <div className='flex flex-col flex-1 min-w-0'>
            {/* Table Search + Upload */}
            <div className='px-6 pt-4'>
                <div className='flex items-center gap-3'>
                    <div className='relative flex-1'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' size={18} />
                        <Input
                            placeholder='Filter items by name or SKU...'
                            className='pl-10 h-10 border-gray-200 focus:bg-white transition-all'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className='flex items-center gap-2'>
                        <input
                            type='file'
                            id='doc-upload'
                            className='hidden'
                            onChange={handleFileUpload}
                            accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg'
                        />
                        <label
                            htmlFor='doc-upload'
                            className='flex items-center gap-2 px-3 h-10 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm text-gray-600'
                        >
                            <Upload size={16} />
                            <span>{uploadedDoc ? uploadedDoc.name : 'Upload Doc'}</span>
                        </label>
                        {uploadedDoc && (
                            <button
                                onClick={handleRemoveDoc}
                                className='text-gray-400 hover:text-red-500 transition-colors'
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    {prDetail?.jsonData?.uploadedDoc && (
                        <button
                            type='button'
                            onClick={() => setShowDocPanel(prev => !prev)}
                            className={`flex items-center gap-2 px-3 h-10 rounded-lg border text-sm font-semibold whitespace-nowrap transition-colors ${showDocPanel
                                ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                                : 'border-blue-200 text-blue-600 hover:bg-blue-50'}`}
                        >
                            <FileText size={16} />
                            View Uploaded Doc
                        </button>
                    )}
                </div>
            </div>
            <div className='h-[calc(95vh-300px)] overflow-auto mt-2'>
                <VariantTable
                    columns={columns}
                    data={filteredItems}
                    customRowData={customRowData}
                    isLoading={isLoadingDetail}
                    customCol={(col) => <div className='text-[12px] flex justify-start font-bold text-gray-500 uppercase ml-3'>{col.title}</div>}
                />
            </div>

            {/* Footer Actions */}
            <div className='p-4 border-t border-gray-100 mt-auto'>
                <div className='flex items-end gap-4'>
                    <div className='flex-1 space-y-1'>
                        <label className='text-xs font-semibold text-gray-500'>Approval / Rejection Reason:</label>
                        <textarea
                            className='w-full h-16 p-2.5 rounded-lg border border-gray-200 focus:ring-1 focus:ring-blue-500 outline-none transition-all resize-none text-sm'
                            placeholder='Enter your reason here...'
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                    <div className='flex gap-2 pb-0.5'>
                        <Button
                            variant='outline'
                            className='bg-red-500 hover:bg-red-600 text-white font-bold h-10 px-6 transition-colors border-none text-sm'
                            onClick={() => showConfirmation('REJECT')}
                            disabled={isUpdating}
                        >
                            Reject
                        </Button>
                        <Button
                            className='bg-green-800 hover:bg-green-900 text-white font-bold h-10 px-6 text-sm'
                            onClick={() => showConfirmation('APPROVE')}
                            disabled={isUpdating}
                        >
                            Approve
                        </Button>
                    </div>
                </div>
            </div>
                </div>

                {/* Uploaded Doc Side Panel */}
                {showDocPanel && prDetail?.jsonData?.uploadedDoc && (
                    <div className='w-[450px] shrink-0 border-l border-gray-200 flex flex-col min-h-0'>
                        <div className='flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/50'>
                            <div className='flex items-center gap-1.5 min-w-0'>
                                <FileText size={15} className='text-blue-600 shrink-0' />
                                <span className='text-sm font-semibold text-gray-700 truncate'>
                                    {prDetail?.jsonData?.uploadedDocFileName || 'Attached Document'}
                                </span>
                            </div>
                            <div className='flex items-center gap-1 shrink-0'>
                                <button
                                    type='button'
                                    title='Download'
                                    onClick={() => downloadPrDoc(
                                        prDetail?.jsonData?.uploadedDoc,
                                        prDetail?.jsonData?.uploadedDocFileName,
                                    )}
                                    className='p-1.5 text-blue-600 hover:text-blue-800 transition-colors'
                                >
                                    <Download size={16} />
                                </button>
                                <button
                                    type='button'
                                    onClick={() => setShowDocPanel(false)}
                                    className='p-1.5 text-gray-400 hover:text-gray-600 transition-colors'
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <div className='flex-1 min-h-0 overflow-hidden'>
                            <PRDocPreview
                                docUrl={prDetail?.jsonData?.uploadedDoc}
                                fileName={prDetail?.jsonData?.uploadedDocFileName}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PRApprovalModalContent;
